import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { GlassCard } from "@/components/GlassCard";
import { Reveal, SectionKicker } from "@/components/Reveal";
import SEO from "@/components/SEO";
import { container, goldBtn } from "@/lib/constants";

const TIERS = [
  {
    name: "Observer", border: "border-border",
    tagline: "Read-only visibility of the deal board.",
    features: ["Curated deal board access", "Sector and mandate briefings", "No introductions", "No document access"],
  },
  {
    name: "Principal", border: "border-[hsl(var(--pdg-gold)/0.4)]", featured: true,
    tagline: "Full access, direct introductions, NDA execution.",
    features: ["Full deal board & detail", "Direct introductions to counterparties", "NDA execution & document access", "ORLA intelligence assistant"],
  },
  {
    name: "Mandate Partner", border: "border-[hsl(var(--pdg-gold)/0.55)]",
    tagline: "Proactive sourcing against your submitted mandate.",
    features: ["Everything in Principal", "Proactive sourcing to your mandate", "Priority introductions", "Weekly deal digest"],
  },
];

const FAQ = [
  ["Do you publish pricing?", "No. Membership terms are discussed privately once your mandate is understood. Speak to the desk."],
  ["Who is eligible?", "Verified principals only — family offices, private investment vehicles, and operators. Verification is required before access."],
  ["Can I upgrade my tier?", "Yes. Tiers reflect your engagement with the desk and can be revisited as your mandate develops."],
];

export default function Membership() {
  const jsonLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q,
      acceptedAnswer: { "@type": "Answer", text: a } })),
  };
  return (
    <PublicLayout>
      <SEO title="Membership" description="Three membership tiers: Observer, Principal, and Mandate Partner. No prices shown publicly — speak to the desk." jsonLd={jsonLd} />
      <section className={`${container} py-20 lg:py-24`}>
        <Reveal>
          <SectionKicker>Membership</SectionKicker>
          <h1 className="pdg-serif text-4xl sm:text-5xl text-foreground max-w-3xl leading-[1.08]">Access levels</h1>
          <p className="mt-6 text-lg text-foreground/80 max-w-2xl leading-8">
            Three tiers, aligned to how you engage with the desk. We do not publish pricing.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.05}>
              <GlassCard className={`p-8 h-full border ${t.border} flex flex-col`}>
                {t.featured && <div className="pdg-kicker text-[hsl(var(--pdg-gold))] mb-3">Most selected</div>}
                <h2 className="pdg-serif text-2xl text-foreground">{t.name}</h2>
                <p className="mt-2 text-sm text-muted-foreground leading-7">{t.tagline}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-foreground/80">
                      <Check size={16} className="mt-0.5 text-[hsl(var(--pdg-gold))]" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/contact" data-testid="membership-speak-to-desk-button" className={goldBtn + " w-full"}>
                    Speak to the desk
                  </Link>
                </div>
              </GlassCard>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 max-w-3xl">
          <Reveal><SectionKicker>Frequently asked</SectionKicker></Reveal>
          <div className="mt-6 space-y-4">
            {FAQ.map(([q, a], i) => (
              <Reveal key={q} delay={i * 0.04}>
                <GlassCard className="p-6">
                  <h3 className="pdg-serif text-lg text-foreground">{q}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-7">{a}</p>
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
